import { useEffect, useRef } from "react";
import type { ModuleName, Permission, PreviewModule } from "@dentacore/shared-types";

/**
 * **The ring per thing** (QR7–QR9, STORY-221).
 *
 * The quick ring no longer carries a fixed list of actions. Each screen tells it
 * what its things are and what can be done to them, because only the screen
 * holds the dialogs those actions open (QR8: a ring is a shortcut to an existing
 * button, never a new feature).
 *
 * - An element that is a *thing* carries `data-ring="<kind>"` — a booking block,
 *   a patient row, a tooth, an inventory row. Anything inside it counts.
 * - The screen registers a provider for that kind with {@link useRingProvider}.
 *   Given the element under the pointer, it returns the thing's ring.
 * - Blank space asks the provider registered for `"page"` — the screen's own
 *   ring (QR7). A screen with no page provider (System) has no ring (WG-D18).
 *
 * Providers stack: the newest mounted wins, so a screen opened over another
 * speaks for itself and the one beneath comes back when it closes.
 */

/** What a workflow can start on from the ring (WG-D1). The server's catalogue uses the same names. */
export type RingSubjectType =
  "booking" | "patient" | "tooth" | "procedure" | "item" | "staff" | "timeoff" | "task" | "expense";

export interface RingAction {
  /** Stable — the person's arrangement (QR11) and a workflow's step (WG-D2) name actions by it. */
  key: string;
  label: string;
  /** An SVG path on a 24-unit box, from `ring-icons.ts`. */
  icon: string;
  /** One short line, shown in the hub while the action is lit. */
  hint: string;
  /** Every one of these must be held, or the action is not drawn (QR5). */
  needs?: Permission[];
  /** An add-on the clinic must have switched on; hidden otherwise (MS-D, QF-D9). */
  module?: ModuleName;
  /** A preview module the clinic must have (TK-D28). */
  preview?: PreviewModule;
  /** Cancel, Retire, Decline… — drawn red when lit (QR9). */
  danger?: boolean;
  run: () => void;
}

export interface RingSpec {
  /**
   * Which ring this is — `"cal.visit"`, `"inv.item"`, `"page.home"`. One name per
   * kind of thing, not per record: the arrangement is kept against it (WG-D19).
   */
  ring: string;
  /** The thing's name, in the hub. */
  title: string;
  /** A second line in the hub: a time, a status, a count. */
  meta?: string;
  /** A ring about a patient shows their critical alert and what they owe (QR10). */
  patientId?: string | null;
  /** The record a workflow would start on from here (WG-D5). */
  subject?: { type: RingSubjectType; id: string } | null;
  /**
   * In order: slot 1 is the next step, *Open* the last (QR9). `false`, `null` and
   * `undefined` are skipped, so a provider can write `live && {…}`.
   */
  actions: (RingAction | false | null | undefined)[];
}

/** Given the element under the pointer, that thing's ring — or null to keep looking outward. */
export type RingProvider = (element: HTMLElement) => RingSpec | null | Promise<RingSpec | null>;

const providers = new Map<string, RingProvider[]>();

/**
 * Register what the ring offers for a kind of thing on this screen, for as long
 * as the component is mounted. The latest `provider` is always the one called,
 * so it may close over the component's state without re-registering.
 */
export function useRingProvider(kind: string, provider: RingProvider): void {
  const latest = useRef(provider);
  latest.current = provider;
  useEffect(() => {
    const stable: RingProvider = (element) => latest.current(element);
    const stack = providers.get(kind) ?? [];
    stack.push(stable);
    providers.set(kind, stack);
    return () => {
      const now = providers.get(kind) ?? [];
      const at = now.lastIndexOf(stable);
      if (at >= 0) now.splice(at, 1);
      if (now.length === 0) providers.delete(kind);
    };
  }, [kind]);
}

/**
 * The ring for whatever is under the pointer: the nearest thing that has a
 * provider, outward from `under`, then the screen's own ring.
 */
export async function findRing(under: Element | null): Promise<RingSpec | null> {
  let node: Element | null = under;
  while (node !== null) {
    const kind = node instanceof HTMLElement ? node.dataset.ring : undefined;
    if (kind !== undefined) {
      const stack = providers.get(kind);
      const provider = stack?.[stack.length - 1];
      if (provider !== undefined) {
        const spec = await provider(node as HTMLElement);
        if (spec !== null) return spec;
      }
    }
    node = node.parentElement;
  }
  const page = providers.get("page");
  const provider = page?.[page.length - 1];
  if (provider === undefined) return null;
  return provider(under instanceof HTMLElement ? under : document.body);
}

/** For tests: forget every provider. */
export function clearRingProviders(): void {
  providers.clear();
}
